/**
 * CID Codes Form Page
 * Page for creating and editing CID-10 codes
 *
 * @version 2025.1.0
 */

import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { cidCodeService } from '../../services/cidCodeService';
import {
  CidCodeCreateFormData,
  CidCodeUpdateFormData,
  CidCategory,
  isValidCidCodeFormat,
} from '../../types/cidCode';

interface FormData {
  code: string;
  description: string;
  category: string;
  subcategory: string;
  isActive: boolean;
}

const CidCodesFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<CidCategory[]>([]);

  const [formData, setFormData] = useState<FormData>({
    code: '',
    description: '',
    category: '',
    subcategory: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await cidCodeService.getCategories();
        setCategories(cats);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Load CID code data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadCidCode(parseInt(id));
    }
  }, [id, isEditMode]);

  const loadCidCode = async (cidCodeId: number) => {
    try {
      setLoading(true);
      setError(null);
      const cidCode = await cidCodeService.getCidCodeById(cidCodeId);

      setFormData({
        code: cidCode.code,
        description: cidCode.description,
        category: cidCode.category,
        subcategory: cidCode.subcategory || '',
        isActive: cidCode.isActive,
      });
    } catch (err: unknown) {
      console.error('Error loading CID code:', err);
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Erro ao carregar código CID-10');
      enqueueSnackbar('Erro ao carregar código CID-10', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Convert code to uppercase
    if (name === 'code') {
      newValue = (value as string).toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Código CID-10 é obrigatório';
    } else if (!isValidCidCodeFormat(formData.code)) {
      newErrors.code = 'Formato inválido. Use: Letra + 2 dígitos + ponto opcional + dígito (ex: I10, M54.9)';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    } else if (formData.description.trim().length < 3) {
      newErrors.description = 'Descrição deve ter pelo menos 3 caracteres';
    }

    if (!formData.category) {
      newErrors.category = 'Categoria é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);

      if (isEditMode && id) {
        const updateData: CidCodeUpdateFormData = {
          code: formData.code.trim().toUpperCase(),
          description: formData.description.trim(),
          category: formData.category,
          subcategory: formData.subcategory.trim() || undefined,
          isActive: formData.isActive,
        };
        await cidCodeService.updateCidCode(parseInt(id), updateData);
        enqueueSnackbar('Código CID-10 atualizado com sucesso!', { variant: 'success' });
      } else {
        const createData: CidCodeCreateFormData = {
          code: formData.code.trim().toUpperCase(),
          description: formData.description.trim(),
          category: formData.category,
          subcategory: formData.subcategory.trim() || undefined,
        };
        await cidCodeService.createCidCode(createData);
        enqueueSnackbar('Código CID-10 registrado com sucesso!', { variant: 'success' });
      }

      navigate('/cid-codes');
    } catch (err: unknown) {
      console.error('Error saving CID code:', err);
      enqueueSnackbar(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error || `Erro ao ${isEditMode ? 'atualizar' : 'registrar'} código CID-10`,
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/cid-codes');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Códigos CID-10', path: '/cid-codes' },
    { label: isEditMode ? 'Editar' : 'Novo Código' },
  ];
  const title = isEditMode ? 'Editar Código CID-10' : 'Novo Código CID-10';

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      actions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Voltar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Code Field */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  required
                  label="Código CID-10"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  error={Boolean(errors.code)}
                  helperText={errors.code || 'Ex: I10, M54.9, Z03.8'}
                  disabled={saving}
                  placeholder="M54.9"
                  inputProps={{
                    style: { textTransform: 'uppercase', fontFamily: 'monospace' },
                    maxLength: 6
                  }}
                />
              </Grid>

              {/* Category Field */}
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required error={Boolean(errors.category)}>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={formData.category}
                    label="Categoria"
                    onChange={(e) => handleSelectChange('category', e.target.value)}
                    disabled={saving}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.code} value={cat.code}>
                        {cat.code} - {cat.description.substring(0, 50)}...
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Subcategory Field */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Subcategoria"
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleInputChange}
                  disabled={saving}
                  placeholder="Ex: M70-M79 (opcional)"
                  helperText="Opcional - subcategoria dentro da categoria principal"
                />
              </Grid>

              {/* Description Field */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Descrição"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={Boolean(errors.description)}
                  helperText={errors.description || 'Descrição completa do diagnóstico em português'}
                  disabled={saving}
                  placeholder="Descrição do código CID-10..."
                />
              </Grid>

              {/* Active Status (only in edit mode) */}
              {isEditMode && (
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        disabled={saving}
                      />
                    }
                    label="Código Ativo"
                  />
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid size={{ xs: 12 }}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : isEditMode ? 'Atualizar' : 'Registrar'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
      </Paper>
    </PageShell>
  );
};

export default CidCodesFormPage;
